import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  InputBase,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  CircularProgress,
  Button,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Chip,
} from '@mui/material';
import {
  Send,
  Add,
  ArrowBack,
  AttachFile,
  Image as ImageIcon,
  Description,
  Download,
  Mic,
  Stop,
  PlayArrow,
  Pause,
  ArrowDropDown,
  Search as SearchIcon,
  Close,
  BroadcastOnPersonal,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { clearThreadUnreadCount } from '../../../Redux/Slices/MessageCountSlice';
import { decryptToken } from '../../../Utlis/Encryption';
import { BASE_URL, URL } from '../../../components/BaseUrl';
import messagingApi from '../../../services/messagingApi';
import socketService from '../../../services/socketService';
import CustomText from '../../../components/CustomText';
import chatimage from '../../../assets/chatimage.jpeg';
import toast from 'react-hot-toast';

const getRoleBadgeLabel = (role, t) => {
  if (!role) return t?.('USER') ?? 'User';
  const r = String(role).toLowerCase();
  if (r === 'account_executive') return t?.('ACCOUNT_EXECUTIVE') ?? 'Acc. Executive';
  if (r === 'company_admin') return t?.('COMPANY_ADMIN') ?? 'Company Admin';
  if (r === 'worker') return t?.('EMPLOYEE') ?? 'Worker';
  if (r === 'super_admin' || r === 'superadmin') return t?.('SUPER_ADMIN') ?? 'Super Admin';
  return t?.('USER') ?? 'User';
};
const getRoleBadgeColor = (role) => {
  if (!role) return '#E5E7EB';
  const r = String(role).toLowerCase();
  if (r === 'account_executive') return '#DBEAFE';
  if (r === 'company_admin') return '#FED7AA';
  if (r === 'worker') return '#D1FAE5';
  if (r === 'super_admin' || r === 'superadmin') return '#FBCFE8';
  return '#E5E7EB';
};
const getRoleBadgeTextColor = (role) => {
  if (!role) return '#374151';
  const r = String(role).toLowerCase();
  if (r === 'account_executive') return '#1D4ED8';
  if (r === 'company_admin') return '#C2410C';
  if (r === 'worker') return '#047857';
  if (r === 'super_admin' || r === 'superadmin') return '#9D174D';
  return '#374151';
};

/** Absolute URLs from API are used as-is; relative paths are prefixed with the app URL. */
const resolveProfileImageUrl = (value) => {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  const base = String(URL).replace(/\/$/, '');
  const path = s.replace(/^\//, '');
  return `${base}/${path}`;
};

const AVATAR_CROSS_ORIGIN_SLOT_PROPS = { img: { crossOrigin: 'anonymous' } };

/** Omit crossOrigin on cross-origin URLs unless the image host sends CORS (otherwise the img fails and Avatar shows initials). */
const avatarSlotPropsForSrc = (src) => {
  if (!src || typeof window === 'undefined') return undefined;
  try {
    const u = src.startsWith('http') ? new URL(src) : new URL(src, window.location.origin);
    if (u.origin === window.location.origin) return AVATAR_CROSS_ORIGIN_SLOT_PROPS;
  } catch {
    /* ignore */
  }
  return undefined;
};

/** Combine thread payload fields with optional worker profile_image map (by other participant id). */
const resolveParticipantAvatarRaw = (thread, isParticipantOne, workerAvatarById) => {
  if (!thread || typeof thread !== 'object') return null;
  let participantId = null;
  const candidates = [];
  if (isParticipantOne) {
    participantId = thread.participant_two_id ?? thread.other_user_id;
    candidates.push(
      thread.participant_two_profile_image,
      thread.participant_two_image,
      thread.other_user_avatar,
      thread.other_user_profile_image,
      thread.profile_image,
      thread.participant_two?.profile_image,
      thread.participant_two_profile_picture,
      thread.participant_two?.face_data?.face_image_url,
    );
  } else {
    participantId = thread.participant_one_id ?? thread.other_user_id;
    candidates.push(
      thread.participant_one_profile_image,
      thread.participant_one_image,
      thread.other_user_avatar,
      thread.other_user_profile_image,
      thread.profile_image,
      thread.participant_one?.profile_image,
      thread.participant_one_profile_picture,
      thread.participant_one?.face_data?.face_image_url,
    );
  }
  candidates.push(thread.face_data?.face_image_url, thread.other_user?.profile_image);
  for (const c of candidates) {
    const u = resolveProfileImageUrl(c);
    if (u) return u;
  }
  if (participantId != null && workerAvatarById?.[String(participantId)]) {
    const u = resolveProfileImageUrl(workerAvatarById[String(participantId)]);
    if (u) return u;
  }
  return null;
};

/** eligible-users / employees responses may use `users`, `records`, or a top-level array. */
const extractEligibleUsersArray = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.users)) return data.users;
  if (Array.isArray(data.records)) return data.records;
  return [];
};

/** Align employee API rows (`first_name`, `last_name`, `face_data`) with chat UI (`name`, `profile_image`). */
const normalizeEligibleUserRecord = (u) => {
  if (!u || typeof u !== 'object') return u;
  const first = u.first_name ?? u.firstName ?? '';
  const last = u.last_name ?? u.lastName ?? '';
  const fromParts = [first, last].filter(Boolean).join(' ').trim();
  const name =
    typeof u.name === 'string' && u.name.trim()
      ? u.name.trim()
      : fromParts || (typeof u.email === 'string' ? u.email : '') || 'Unknown';
  const profile_image =
    u.profile_image ||
    u.profileImage ||
    u.face_data?.face_image_url ||
    u.faceData?.face_image_url ||
    undefined;
  return { ...u, name, ...(profile_image ? { profile_image } : {}) };
};

const UnifiedMessages = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const company = useSelector((state) => state.auth.company);
  const companyId = company?.id;
  const userId = user?.id;

  // State management
  const [selectedChatType, setSelectedChatType] = useState(null); // null, 'super_admin', 'account_executive', 'worker'
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [threadSearchTerm, setThreadSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [showUserList, setShowUserList] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  // Upload states (following documentation)
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState(null);
  const [uploadedAudioDuration, setUploadedAudioDuration] = useState(0);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [audioProgressById, setAudioProgressById] = useState({});
  const [showBroadcastDialog, setShowBroadcastDialog] = useState(false);
  const [broadcastWorkers, setBroadcastWorkers] = useState([]);
  const [selectedWorkersForBroadcast, setSelectedWorkersForBroadcast] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isSendingBroadcast, setIsSendingBroadcast] = useState(false);
  const [showChatTypeDialog, setShowChatTypeDialog] = useState(false);
  const [userStatuses, setUserStatuses] = useState({}); // { userId: { isOnline: boolean } }
  const [selectedWorkers, setSelectedWorkers] = useState([]); // For multi-select workers
  const [multiSelectMessage, setMultiSelectMessage] = useState(''); // Message for multiple workers
  const [isSendingMultiMessage, setIsSendingMultiMessage] = useState(false);
  /** Maps worker/other participant id -> raw profile_image from GET /public/workers (thread payloads often omit avatars). */
  const [workerAvatarById, setWorkerAvatarById] = useState({});
  // Message list pagination: only "load older" (previous page in time)
  const MESSAGES_PER_PAGE = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [totalMessages, setTotalMessages] = useState(null); // optional, from API

  const messagesEndRef = useRef(null);
  const isLoadingOlderRef = useRef(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  // Audio management refs (following documentation)
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});
  const audioBlobUrlsRef = useRef({});
  const audioDurationLoadingRef = useRef(new Set());
  const audioDurationLoadedRef = useRef(new Set());
  const selectedThreadRef = useRef(null);
  const mediaStreamRef = useRef(null); // Store stream separately
  const processedOpenChatKeyRef = useRef(null);

  // Chat type options
  const chatTypeOptions = [
    { value: 'super_admin', label: 'Super Admin' },
    { value: 'account_executive', label: 'Account Executive' },
    { value: 'worker', label: 'Worker' },
  ];

  // Get decrypted token for API calls
  const decryptedToken = token ? decryptToken(token) : null;

  useEffect(() => {
    if (!decryptedToken || !companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${BASE_URL}/public/workers?company_id=${companyId}&no_pagination=true&status=active`
        );
        const data = await res.json();
        if (cancelled || data.error || !Array.isArray(data.data?.records)) return;
        const map = {};
        for (const w of data.data.records) {
          if (w?.id == null) continue;
          const pic = w.profile_image || w.face_data?.face_image_url;
          if (pic) map[String(w.id)] = pic;
        }
        setWorkerAvatarById(map);
      } catch (e) {
        console.error('UnifiedMessages: could not load worker profile images map', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [decryptedToken, companyId]);

  // Upload functions (following documentation)
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${URL}/api/upload/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${decryptedToken}` },
      body: formData,
    });

    const result = await response.json();

    if (!result.error && result.data?.url) {
      return result.data.url;
    } else {
      throw new Error(result.message || 'Image upload failed');
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await fetch(`${URL}/api/upload/pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${decryptedToken}` },
      body: formData,
    });

    const result = await response.json();

    if (!result.error && result.data?.url) {
      return result.data.url;
    } else if (!result.error && result.url) {
      return result.url;
    } else {
      throw new Error(result.message || 'File upload failed');
    }
  };

  const uploadAudio = async (audioBlob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'audio.webm');

    const response = await fetch(`${URL}/api/upload/audio`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${decryptedToken}` },
      body: formData,
    });

    const result = await response.json();

    if (!result.error && result.data?.url) {
      return result.data.url;
    } else if (!result.error && result.url) {
      return result.url;
    } else {
      throw new Error(result.message || 'Audio upload failed');
    }
  };

  // Initialize socket connection
  useEffect(() => {
    if (token) {
      socketService.connect();
      
      // Setup authentication handler
      const socket = socketService.getSocket();
      if (socket) {
        const handleAuthenticated = (data) => {
          console.log('Socket authenticated:', data);
          if (data.user?.id) {
            socketService.joinUserRoom(data.user.id);
            socketService.getUserStatuses();
          }
        };
        
        socket.on('authenticated', handleAuthenticated);
        
        return () => {
          socket.off('authenticated', handleAuthenticated);
        };
      }
    }

    return () => {
      socketService.disconnect();
    };
  }, [token]);

  // Setup socket listeners
  useEffect(() => {
    // Handle receive_message event
    const handleReceiveMessage = (data) => {
      console.log('Received message via socket:', data);
      const message = data.message || data;
      const threadId = data.threadId || data.thread_id || message.thread_id;
      
      if (!threadId) {
        console.warn('No threadId found in received message:', data);
        return;
      }
      
      // Skip if this is our own message that we just sent (we already have it from API)
      const isOwnMessage = message.sender_id === userId || String(message.sender_id) === String(userId);
      
      // Check if message is for current thread
      if (selectedThread && String(threadId) === String(selectedThread.id)) {
        // Add message to current thread (append to end, maintain sorted order)
        setMessages((prev) => {
          // Enhanced duplicate detection
          const exists = prev.some((msg) => {
            // Check by ID
            if (msg.id && message.id && String(msg.id) === String(message.id)) return true;
            // Check by tempId
            if (msg.tempId && message.tempId && msg.tempId === message.tempId) return true;
            // Check by content, sender, and timestamp (within 2 seconds) for duplicate detection
            if (msg.content === message.content && 
                msg.sender_id === message.sender_id &&
                msg.created_at && message.created_at) {
              const timeDiff = Math.abs(new Date(msg.created_at) - new Date(message.created_at));
              if (timeDiff < 2000) return true; // Same message within 2 seconds
            }
            return false;
          });
          
          if (exists) {
            console.log('Duplicate message detected, skipping:', message.id || message.tempId);
            return prev;
          }
          
          // If it's our own message and we have a temp message, replace it instead of adding
          if (isOwnMessage && message.tempId) {
            const hasTemp = prev.some((msg) => msg.tempId === message.tempId || msg.id === message.tempId);
            if (hasTemp) {
              if (message.id) {
                setAudioProgressById((prevProgress) => {
                  if (!prevProgress[message.tempId]?.duration || prevProgress[message.id]) return prevProgress;
                  return {
                    ...prevProgress,
                    [message.id]: prevProgress[message.tempId],
                  };
                });
              }
              // Replace temp message with actual message
              return prev.map((msg) => 
                (msg.tempId === message.tempId || msg.id === message.tempId) ? message : msg
              ).filter((msg, index, self) => 
                // Remove any duplicates after replacement
                index === self.findIndex((m) => 
                  (m.id && msg.id && String(m.id) === String(msg.id)) ||
                  (m.tempId && msg.tempId && m.tempId === msg.tempId)
                )
              );
            }
          }
          
          // Add new message and sort to maintain chronological order
          const updated = [...prev, message];
          return updated.sort((a, b) => {
            const aTime = new Date(a.created_at || a.createdAt || 0);
            const bTime = new Date(b.created_at || b.createdAt || 0);
            return aTime - bTime; // Ascending order (oldest first)
          });
        });
        scrollToBottom();
        // Mark as read if it's the current thread and not from current user
        if (!isOwnMessage) {
          messagingApi.markAsRead(selectedThread.id);
        }
        // Keep sidebar preview in sync (socket path; same thread was previously skipped here)
        updateThreadInList(threadId, message, { skipUnreadIncrement: !isOwnMessage });
      } else {
        // Message is from another thread - update thread list
        updateThreadInList(threadId, message);
        
        // If thread doesn't exist in list, we might need to reload threads
        setThreads((prev) => {
          const threadExists = prev.some((t) => String(t.id) === String(threadId));
          if (!threadExists) {
            // Thread doesn't exist, reload threads to get it
            setTimeout(() => loadThreads(), 500);
          }
          return prev;
        });
      }
    };

    socketService.onMessage(handleReceiveMessage);
    
    // Also listen for new_message event (alternative)
    socketService.onNewMessage(handleReceiveMessage);

    socketService.onMessageSent((data) => {
      console.log('Message sent confirmation via socket:', data);
      // Replace temp message with actual message from server
      if (data.tempId || (data.message && data.message.tempId)) {
        const tempId = data.tempId || data.message?.tempId;
        if (data.message?.id) {
          setAudioProgressById((prevProgress) => {
            if (!prevProgress[tempId]?.duration || prevProgress[data.message.id]) return prevProgress;
            return {
              ...prevProgress,
              [data.message.id]: prevProgress[tempId],
            };
          });
        }
        setMessages((prev) => {
          // Remove temp message and add actual message
          const filtered = prev.filter((msg) => 
            msg.tempId !== tempId && 
            msg.id !== tempId
          );
          
          // Check if actual message already exists
          if (data.message?.id) {
            const messageExists = filtered.some((msg) => 
              msg.id && String(msg.id) === String(data.message.id)
            );
            
            if (!messageExists && data.message) {
              const updated = [...filtered, data.message];
              // Sort to maintain chronological order
              return updated.sort((a, b) => {
                const aTime = new Date(a.created_at || a.createdAt || 0);
                const bTime = new Date(b.created_at || b.createdAt || 0);
                return aTime - bTime; // Ascending order (oldest first)
              });
            }
          }
          
          return filtered;
        });
        if (data.message) {
          const tid =
            data.threadId || data.thread_id || data.message.thread_id || selectedThread?.id;
          if (tid) {
            updateThreadInList(tid, data.message);
          }
        }
      }
    });

    socketService.onUnreadCountUpdate((data) => {
      // Update unread counts
      if (data.threadId) {
        setThreads((prev) =>
          prev.map((thread) =>
            String(thread.id) === String(data.threadId)
              ? { 
                  ...thread, 
                  unread_count: data.unreadCount || data.unread_count || 0,
                  unreadCount: data.unreadCount || data.unread_count || 0,
                }
              : thread
          )
        );
      }
    });

    // User status change listener
    socketService.onUserStatusChange((data) => {
      console.log('User status changed:', data);
      setUserStatuses((prev) => ({
        ...prev,
        [data.userId]: { isOnline: data.status === 'online' },
      }));
    });

    // User statuses list listener
    socketService.onUserStatusesList((data) => {
      console.log('User statuses list:', data);
      if (data.users && Array.isArray(data.users)) {
        const statusMap = {};
        data.users.forEach((user) => {
          statusMap[user.userId] = { isOnline: user.isOnline || false };
        });
        setUserStatuses(statusMap);
      }
    });

    return () => {
      socketService.offMessage();
      socketService.offNewMessage();
      socketService.offMessageSent();
      socketService.offUnreadCountUpdate();
      socketService.offUserStatusChange();
      socketService.offUserStatusesList();
    };
  }, [selectedThread, userId]);

  // Load all threads on mount and when needed
  useEffect(() => {
    loadThreads();
  }, []); // Load all threads on mount

  // Open chat directly from navigation state (e.g., Workers table → Chat)
  const openChatReceiverId = location.state?.openChat?.receiverId;
  const openChatReceiverRole = location.state?.openChat?.receiverRole;

  useEffect(() => {
    if (!openChatReceiverId) return;

    // Prevent re-processing the same navigation event
    if (processedOpenChatKeyRef.current === location.key) return;
    processedOpenChatKeyRef.current = location.key;

    const openFromNavigation = async () => {
      try {
        // Create (or fetch existing) thread with this receiver
        const threadResponse = await messagingApi.createThread(
          openChatReceiverId,
          openChatReceiverRole || 'worker'
        );

        if (threadResponse.error) {
          throw new Error(threadResponse.message || 'Failed to create thread');
        }

        const threadId = threadResponse.data?.thread?.id;
        if (!threadId) {
          throw new Error('No thread id returned from server');
        }

        // Fetch full thread details so we can select it immediately
        const threadDetailsResponse = await messagingApi.getThreadDetails(threadId);
        if (threadDetailsResponse.error) {
          throw new Error(threadDetailsResponse.message || 'Failed to load thread details');
        }

        const threadData = threadDetailsResponse.data;
        const newThread = {
          id: threadData.id,
          participant_one_id: threadData.participant_one_id,
          participant_two_id: threadData.participant_two_id,
          participant_one_name: threadData.participant_one_name,
          participant_two_name: threadData.participant_two_name,
          participant_one_role: threadData.participant_one_role,
          participant_two_role: threadData.participant_two_role,
          participant_one_profile_image:
            threadData.participant_one_profile_image || threadData.participant_one_image,
          participant_two_profile_image:
            threadData.participant_two_profile_image || threadData.participant_two_image,
          unread_count: 0,
          last_message: null,
          created_at: threadData.created_at,
          updated_at: threadData.updated_at,
        };

        // Refresh sidebar thread list then open the thread
        await loadThreads();
        setSelectedThread(newThread);
      } catch (error) {
        console.error('Error opening chat from navigation:', error);
        toast.error(error.message || 'Failed to open chat');
      }
    };

    openFromNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, openChatReceiverId, openChatReceiverRole]);

  // Load messages when thread is selected
  useEffect(() => {
    selectedThreadRef.current = selectedThread;
    if (selectedThread) {
      fetchMessages(selectedThread.id, 1, true);
      joinThreadRoom();
      markThreadAsRead();
    } else {
      setMessages([]);
      setCurrentPage(1);
      setHasMoreMessages(false);
    }
  }, [selectedThread]);

  // Scroll to bottom when messages change, but not when loading older (prepend)
  useEffect(() => {
    if (!isLoadingOlderRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  // Cleanup audio refs on unmount
  useEffect(() => {
    return () => {
      // Cleanup all audio elements
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      // Cleanup blob URLs
      Object.values(audioBlobUrlsRef.current).forEach((blobUrl) => {
        if (blobUrl) {
          window.URL.revokeObjectURL(blobUrl);
        }
      });
      // Stop recording if active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      // Stop stream tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Load all threads (inbox) - not filtered by role
  const loadThreads = async () => {
    setIsLoadingThreads(true);
    try {
      const response = await messagingApi.getThreads({ page: 1, limit: 50 });

      if (response.error) {
        throw new Error(response.message);
      }

      const threadsData = response.data?.threads || [];
      console.log('Loaded threads:', threadsData);
      console.log('Current userId:', userId);
      
      // Log first thread structure for debugging
      if (threadsData.length > 0) {
        console.log('Sample thread structure:', threadsData[0]);
        const sampleParticipant = getParticipantInfo(threadsData[0]);
        console.log('Sample participant info:', sampleParticipant);
      }
      
      // Normalize thread data structure - handle different API response formats
      const normalizedThreads = threadsData.map((thread) => {
        // Handle last_message - could be object or we need to construct it
        let lastMessage = thread.last_message;
        if (!lastMessage && (thread.last_message_content || thread.last_message_at)) {
          // Construct last_message object if API returns separate fields
          lastMessage = {
            id: thread.last_message_id,
            content: thread.last_message_content || '',
            message_type: thread.last_message_type || thread.lastMessageType || 'text',
            created_at: thread.last_message_at || thread.lastMessageAt || thread.updated_at || thread.created_at,
            sender_id: thread.last_message_sender_id || thread.lastMessageSenderId,
            sender_name: thread.last_message_sender_name || thread.lastMessageSenderName,
          };
        } else if (lastMessage && typeof lastMessage === 'object') {
          // Ensure last_message has all required fields
          lastMessage = {
            ...lastMessage,
            content: lastMessage.content || lastMessage.msg || '',
            message_type: lastMessage.message_type || lastMessage.type || lastMessage.messageType || 'text',
            created_at: lastMessage.created_at || lastMessage.createdAt || thread.updated_at || thread.created_at,
          };
        }
        
        // Handle unread_count - check multiple field name variations
        const unreadCount = thread.unread_count !== undefined 
          ? thread.unread_count 
          : (thread.unreadCount !== undefined ? thread.unreadCount : 0);
        
        return {
          ...thread,
          last_message: lastMessage,
          unread_count: Number(unreadCount) || 0, // Ensure it's a number
        };
      });
      
      // Sort threads by updated_at or last_message.created_at (most recent first)
      const sortedThreads = normalizedThreads.sort((a, b) => {
        const aTime = a.last_message?.created_at || a.last_message_at || a.updated_at || a.created_at || '';
        const bTime = b.last_message?.created_at || b.last_message_at || b.updated_at || b.created_at || '';
        return new Date(bTime) - new Date(aTime);
      });
      
      setThreads(sortedThreads);
      
      // Request user statuses after loading threads
      if (socketService.getConnectionStatus()) {
        socketService.getUserStatuses();
      }
    } catch (error) {
      console.error('Error loading threads:', error);
      toast.error(error.message || 'Failed to load threads');
    } finally {
      setIsLoadingThreads(false);
    }
  };

  // Fetch messages: resetList true = replace (open/switch chat), false = prepend (load older)
  const fetchMessages = async (threadId, page, resetList) => {
    if (!threadId) return;
    if (resetList) setIsLoadingMessages(true);

    try {
      const response = await messagingApi.getMessages(threadId, {
        page,
        limit: MESSAGES_PER_PAGE,
      });

      if (response.error) {
        throw new Error(response.message);
      }

      const messagesList = response.data?.messages || [];
      const pagination = response.data?.pagination || response.data;
      const total = pagination?.total_count ?? pagination?.total ?? null;
      const hasNext = pagination?.has_next_page ?? (messagesList.length === MESSAGES_PER_PAGE);

      // API returns newest first (page 1 = most recent). Sort by created_at ascending (oldest → newest) for display.
      const sortByCreatedAt = (a, b) => {
        const aTime = new Date(a.created_at || a.createdAt || 0);
        const bTime = new Date(b.created_at || b.createdAt || 0);
        return aTime - bTime;
      };
      const sortedNew = [...messagesList].sort(sortByCreatedAt);

      if (resetList) {
        setMessages(sortedNew);
        setCurrentPage(1);
        setHasMoreMessages(!!hasNext);
        if (total != null) setTotalMessages(total);
      } else {
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => String(m.id)));
          const toPrepend = sortedNew.filter((m) => !existingIds.has(String(m.id)));
          const merged = [...toPrepend, ...prev];
          return merged.sort(sortByCreatedAt);
        });
        setCurrentPage(page);
        setHasMoreMessages(!!hasNext);
        if (total != null) setTotalMessages(total);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error(error.message || 'Failed to load messages');
    } finally {
      if (resetList) setIsLoadingMessages(false);
    }
  };

  const loadMoreMessages = async () => {
    if (isLoadingMoreMessages || !hasMoreMessages || !selectedThread?.id) return;
    isLoadingOlderRef.current = true;
    setIsLoadingMoreMessages(true);
    try {
      await fetchMessages(selectedThread.id, currentPage + 1, false);
    } finally {
      setIsLoadingMoreMessages(false);
      setTimeout(() => {
        isLoadingOlderRef.current = false;
      }, 100);
    }
  };

  // Join thread room for real-time updates
  const joinThreadRoom = () => {
    if (selectedThread) {
      socketService.joinThreadRoom(selectedThread.id);
    }
  };

  // Mark thread as read (API + local state + Redux so sidebar count updates)
  const markThreadAsRead = async () => {
    if (!selectedThread) return;

    const threadId = String(selectedThread.id);
    dispatch(clearThreadUnreadCount({ threadId }));

    try {
      await messagingApi.markAsRead(selectedThread.id);
      const socket = socketService.getSocket();
      if (socket?.connected) {
        socket.emit('mark_read', { threadId: selectedThread.id });
      }
      setThreads((prev) =>
        prev.map((thread) =>
          String(thread.id) === threadId
            ? { ...thread, unread_count: 0, unreadCount: 0 }
            : thread
        )
      );
    } catch (error) {
      console.error('Error marking thread as read:', error);
    }
  };

  // Update thread in list when new message arrives
  const updateThreadInList = (threadId, message, options = {}) => {
    const { skipUnreadIncrement = false } = options;
    setThreads((prev) => {
      const threadIndex = prev.findIndex((t) => String(t.id) === String(threadId));
      
      if (threadIndex !== -1) {
        // Thread exists - update it and move to top
        const updated = prev.map((thread) => {
          if (String(thread.id) === String(threadId)) {
            const currentUnreadCount = thread.unread_count || thread.unreadCount || 0;
            const isFromOtherUser =
              message.sender_id != null &&
              userId != null &&
              String(message.sender_id) !== String(userId);
            const nextUnread =
              skipUnreadIncrement || !isFromOtherUser
                ? currentUnreadCount
                : currentUnreadCount + 1;

            return {
              ...thread,
              last_message: message,
              unread_count: nextUnread,
              unreadCount: nextUnread,
              updated_at: message.created_at || message.createdAt || thread.updated_at,
            };
          }
          return thread;
        });
        
        // Move updated thread to top (most recent first)
        const updatedThread = updated[threadIndex];
        const rest = updated.filter((t, i) => i !== threadIndex);
        return [updatedThread, ...rest];
      } else {
        // Thread doesn't exist - reload threads to get it
        console.log('Thread not found in list, reloading threads...');
        setTimeout(() => loadThreads(), 500);
        return prev;
      }
    });
  };

  // Handle chat type selection - opens user list dialog
  const handleChatTypeSelect = (chatType) => {
    setSelectedChatType(chatType);
    setShowChatTypeDialog(false);
    setShowUserList(true);
    setUserSearchTerm('');
    setSelectedWorkers([]); // Reset selected workers
    setMultiSelectMessage(''); // Reset message
  };

  // Handle thread selection
  const handleThreadSelect = (thread) => {
    setSelectedThread(thread);
    if (isMobile) {
      // On mobile, you might want to hide thread list
    }
  };

  // Load eligible users for creating new chat
  const loadEligibleUsers = async (search = '') => {
    setIsLoadingUsers(true);
    try {
      const roleMap = {
        super_admin: 'super_admin',
        account_executive: 'account_executive',
        worker: 'worker',
      };

      const response = await messagingApi.getEligibleUsers({
        role: roleMap[selectedChatType],
        search: search,
        noPagination: true,
      });

      if (response.error) {
        throw new Error(response.message);
      }

      const rawList = extractEligibleUsersArray(response.data);
      setEligibleUsers(rawList.map(normalizeEligibleUserRecord));
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(error.message || 'Failed to load users');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Load users when user list is opened
  useEffect(() => {
    if (showUserList && selectedChatType) {
      loadEligibleUsers('');
    }
  }, [showUserList, selectedChatType]);

  // Load workers for broadcast
  const loadBroadcastWorkers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await messagingApi.getEligibleUsers({
        role: 'worker',
        noPagination: true,
      });

      if (response.error) {
        throw new Error(response.message);
      }

      const rawList = extractEligibleUsersArray(response.data);
      setBroadcastWorkers(rawList.map(normalizeEligibleUserRecord));
    } catch (error) {
      console.error('Error loading workers for broadcast:', error);
      toast.error(error.message || 'Failed to load workers');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Send broadcast message
  const sendBroadcast = async () => {
    if (!broadcastMessage.trim() || selectedWorkersForBroadcast.length === 0) {
      toast.error('Please select workers and enter a message');
      return;
    }

    setIsSendingBroadcast(true);
    try {
      // Keep IDs as strings for broadcast endpoint compatibility.
      const workerIds = selectedWorkersForBroadcast.map((w) => String(w.id));
      let sentCount = 0;

      const response = await messagingApi.sendBroadcast(workerIds, broadcastMessage, 'text');

      if (response.error) {
        throw new Error(response.message || 'Broadcast endpoint failed');
      }

      sentCount = response.data?.sentCount || workerIds.length;
      toast.success(`Broadcast sent to ${sentCount} workers`);
      setShowBroadcastDialog(false);
      setBroadcastMessage('');
      setSelectedWorkersForBroadcast([]);
      // Reload threads to show new broadcast threads
      loadThreads();
    } catch (error) {
      const msg = String(error?.message || '');
      const isTypeMismatchError =
        msg.includes('operator does not exist') &&
        msg.includes('character varying = integer');

      if (isTypeMismatchError) {
        // Fallback path: use stable per-worker flow so feature still works.
        let successCount = 0;
        let failCount = 0;

        for (const worker of selectedWorkersForBroadcast) {
          try {
            const threadResponse = await messagingApi.createThread(worker.id, 'worker');
            if (threadResponse.error) {
              throw new Error(threadResponse.message);
            }

            const threadId = threadResponse.data?.thread?.id;
            if (!threadId) {
              throw new Error('Thread ID not found');
            }

            const messageResponse = await messagingApi.sendMessage(threadId, broadcastMessage, 'text');
            if (messageResponse.error) {
              throw new Error(messageResponse.message);
            }

            successCount++;
          } catch (sendError) {
            console.error(`Broadcast fallback failed for worker ${worker?.id}:`, sendError);
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`Broadcast sent to ${successCount} workers`);
          setShowBroadcastDialog(false);
          setBroadcastMessage('');
          setSelectedWorkersForBroadcast([]);
          await loadThreads();
        }

        if (failCount > 0) {
          toast.error(`Failed to send to ${failCount} worker(s)`);
        }
      } else {
        console.error('Error sending broadcast:', error);
        toast.error(error.message || 'Failed to send broadcast');
      }
    } finally {
      setIsSendingBroadcast(false);
    }
  };

  // Handle worker checkbox toggle
  const handleWorkerToggle = (worker) => {
    setSelectedWorkers((prev) => {
      const isSelected = prev.some((w) => w.id === worker.id);
      if (isSelected) {
        return prev.filter((w) => w.id !== worker.id);
      } else {
        return [...prev, worker];
      }
    });
  };

  // Handle select all workers
  const handleSelectAllWorkers = () => {
    if (selectedChatType === 'worker' && eligibleUsers.length > 0) {
      const allSelected = eligibleUsers.every((user) =>
        selectedWorkers.some((w) => w.id === user.id)
      );
      
      if (allSelected) {
        // Deselect all
        setSelectedWorkers([]);
        setMultiSelectMessage('');
      } else {
        // Select all
        setSelectedWorkers([...eligibleUsers]);
      }
    }
  };

  // Handle starting chat with single worker
  const handleStartSingleChat = async () => {
    if (selectedWorkers.length !== 1) return;

    const worker = selectedWorkers[0];
    try {
      setIsLoadingUsers(true);
      const response = await messagingApi.createThread(worker.id);
      if (response.error) {
        throw new Error(response.message);
      }

      const threadDetailsResponse = await messagingApi.getThreadDetails(response.data.thread.id);
      if (threadDetailsResponse.error) {
        throw new Error(threadDetailsResponse.message);
      }

      const threadData = threadDetailsResponse.data;
      const newThread = {
        id: threadData.id,
        participant_one_id: threadData.participant_one_id,
        participant_two_id: threadData.participant_two_id,
        participant_one_name: threadData.participant_one_name,
        participant_two_name: threadData.participant_two_name,
        participant_one_role: threadData.participant_one_role,
        participant_two_role: threadData.participant_two_role,
        participant_one_profile_image: threadData.participant_one_profile_image || threadData.participant_one_image,
        participant_two_profile_image: threadData.participant_two_profile_image || threadData.participant_two_image,
        unread_count: 0,
        last_message: null,
        created_at: threadData.created_at,
        updated_at: threadData.updated_at,
      };

      await loadThreads();
      setSelectedThread(newThread);
      setShowUserList(false);
      setSelectedChatType(null);
      setUserSearchTerm('');
      setEligibleUsers([]);
      setSelectedWorkers([]);
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error(error.message || 'Failed to create thread');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Send message to multiple workers
  const sendMessageToMultipleWorkers = async () => {
    if (!multiSelectMessage.trim() || selectedWorkers.length === 0) {
      toast.error('Please enter a message and select at least one worker');
      return;
    }

    setIsSendingMultiMessage(true);
    const successCount = { count: 0 };
    const errorCount = { count: 0 };

    try {
      // Loop through each selected worker
      for (const worker of selectedWorkers) {
        try {
          // Create or get thread for this worker
          const threadResponse = await messagingApi.createThread(worker.id);
          if (threadResponse.error) {
            throw new Error(threadResponse.message);
          }

          const threadId = threadResponse.data.thread.id;

          // Send message via API
          const messageResponse = await messagingApi.sendMessage(
            threadId,
            multiSelectMessage,
            'text'
          );

          if (messageResponse.error) {
            throw new Error(messageResponse.message);
          }
          // NOTE:
          // Multi-send should use ONE transport only. Sending via API + socket causes duplicates.
          // We send via API and rely on socket events for real-time updates.

          successCount.count++;
        } catch (error) {
          console.error(`Error sending message to worker ${worker.name}:`, error);
          errorCount.count++;
        }
      }

      // Show success/error summary
      if (successCount.count > 0) {
        toast.success(`Message sent to ${successCount.count} worker(s)`);
      }
      if (errorCount.count > 0) {
        toast.error(`Failed to send message to ${errorCount.count} worker(s)`);
      }

      // Reset state and close dialog
      setMultiSelectMessage('');
      setSelectedWorkers([]);
      setShowUserList(false);
      setSelectedChatType(null);
      setUserSearchTerm('');
      setEligibleUsers([]);

      // Reload threads to show new conversations
      await loadThreads();
    } catch (error) {
      console.error('Error sending messages to multiple workers:', error);
      toast.error(error.message || 'Failed to send messages');
    } finally {
      setIsSendingMultiMessage(false);
    }
  };

  // Handle user selection to create new thread
  const handleUserSelect = async (user) => {
    // If worker is selected and we have multiple selection mode, handle differently
    if (selectedChatType === 'worker') {
      // Toggle worker selection instead of immediately creating thread
      handleWorkerToggle(user);
      return;
    }

    // For non-worker roles (account_executive, super_admin), create thread with correct receiver role
    try {
      setIsLoadingUsers(true);
      // Map UI chat type to API receiverRole (backend may expect 'superadmin' not 'super_admin')
      const roleForApi = selectedChatType === 'super_admin' ? 'superadmin' : (selectedChatType || 'worker');
      const response = await messagingApi.createThread(user.id, roleForApi);
      if (response.error) {
        throw new Error(response.message);
      }

      // Get full thread details
      const threadDetailsResponse = await messagingApi.getThreadDetails(response.data.thread.id);
      if (threadDetailsResponse.error) {
        throw new Error(threadDetailsResponse.message);
      }

      const threadData = threadDetailsResponse.data;
      const newThread = {
        id: threadData.id,
        participant_one_id: threadData.participant_one_id,
        participant_two_id: threadData.participant_two_id,
        participant_one_name: threadData.participant_one_name,
        participant_two_name: threadData.participant_two_name,
        participant_one_role: threadData.participant_one_role,
        participant_two_role: threadData.participant_two_role,
        participant_one_profile_image: threadData.participant_one_profile_image || threadData.participant_one_image,
        participant_two_profile_image: threadData.participant_two_profile_image || threadData.participant_two_image,
        unread_count: 0,
        last_message: null,
        created_at: threadData.created_at,
        updated_at: threadData.updated_at,
      };

      // Reload threads to get updated list
      await loadThreads();
      
      // Select the new thread
      setSelectedThread(newThread);
      setShowUserList(false);
      setSelectedChatType(null); // Reset chat type selection
      setUserSearchTerm('');
      setEligibleUsers([]);
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error(error.message || 'Failed to create thread');
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Helper function to get full file URL
  const getFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      return fileUrl;
    }
    return `${URL}${fileUrl.startsWith('/') ? fileUrl.substring(1) : fileUrl}`;
  };

  // Format duration helper
  const formatDuration = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00';
    if (seconds < 1) return Number(seconds).toFixed(1);
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getAudioDurationFromBlob = (blob) =>
    new Promise((resolve) => {
      if (!blob) {
        resolve(0);
        return;
      }

      const blobUrl = window.URL.createObjectURL(blob);
      const probeAudio = new Audio();
      probeAudio.preload = 'metadata';
      probeAudio.src = blobUrl;

      let settled = false;
      let timeoutId = null;
      let seekAttempted = false;
      const cleanup = () => {
        probeAudio.onloadedmetadata = null;
        probeAudio.ondurationchange = null;
        probeAudio.ontimeupdate = null;
        probeAudio.onerror = null;
        try {
          probeAudio.pause();
        } catch (_) {}
        if (timeoutId) clearTimeout(timeoutId);
        window.URL.revokeObjectURL(blobUrl);
      };
      const finalize = (duration) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(isFinite(duration) && duration > 0 ? duration : 0);
      };
      const handleReady = () => {
        const duration = Number(probeAudio.duration);
        if (isFinite(duration) && duration > 0) {
          finalize(duration);
          return;
        }
        // MediaRecorder WebM files report Infinity for duration until the
        // media is seeked to the end. Force a seek so the browser computes
        // the real length, then read it via ontimeupdate / durationchange.
        if (!seekAttempted) {
          seekAttempted = true;
          probeAudio.ontimeupdate = () => {
            probeAudio.ontimeupdate = null;
            const d = Number(probeAudio.duration);
            try {
              probeAudio.currentTime = 0;
            } catch (_) {}
            finalize(d);
          };
          try {
            probeAudio.currentTime = 1e9;
          } catch (_) {
            finalize(probeAudio.duration);
          }
        }
      };

      probeAudio.onloadedmetadata = handleReady;
      probeAudio.ondurationchange = handleReady;
      probeAudio.onerror = () => finalize(0);
      // Generous timeout — the seek round-trip can take longer than 1.2 s on slower devices.
      timeoutId = setTimeout(() => finalize(0), 4000);
      probeAudio.load();
    });

  // Preload audio metadata so duration is visible before first play
  useEffect(() => {
    if (!messages.length || !decryptedToken) return;

    const preloadAudioDuration = async (msgId, audioUrl) => {
      if (!msgId || !audioUrl) return;
      if (audioDurationLoadingRef.current.has(msgId)) return;
      if (audioDurationLoadedRef.current.has(msgId)) return;

      audioDurationLoadingRef.current.add(msgId);

      try {
        const response = await fetch(audioUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            Accept: 'audio/webm, audio/*',
          },
          mode: 'cors',
        });

        if (!response.ok) {
          audioDurationLoadingRef.current.delete(msgId);
          return;
        }

        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const metadataAudio = new Audio();
        metadataAudio.preload = 'metadata';

        let finalized = false;
        let fallbackTimer = null;
        let seekAttempted = false;

        const cleanupMetadataAudio = () => {
          metadataAudio.onloadedmetadata = null;
          metadataAudio.ondurationchange = null;
          metadataAudio.ontimeupdate = null;
          metadataAudio.onerror = null;
          try {
            metadataAudio.pause();
          } catch (_) {}
          try {
            metadataAudio.src = '';
          } catch (_) {}
          if (fallbackTimer) {
            clearTimeout(fallbackTimer);
            fallbackTimer = null;
          }
        };
        const finalizeMetadata = (duration) => {
          if (finalized) return;
          finalized = true;
          const safeDuration = isFinite(duration) && duration > 0 ? duration : 0;
          if (safeDuration > 0) {
            setAudioProgressById((prev) => ({
              ...prev,
              [msgId]: {
                current: prev[msgId]?.current || 0,
                duration: safeDuration,
              },
            }));
            audioDurationLoadedRef.current.add(msgId);
          }
          cleanupMetadataAudio();
          window.URL.revokeObjectURL(blobUrl);
          audioDurationLoadingRef.current.delete(msgId);
        };
        const tryResolveMetadata = () => {
          const duration = Number(metadataAudio.duration);
          if (isFinite(duration) && duration > 0) {
            finalizeMetadata(duration);
            return;
          }
          // WebM produced by MediaRecorder reports Infinity for duration
          // until the media is seeked to the end. Force a seek so the
          // browser walks to EOF and yields the real length via timeupdate.
          if (!seekAttempted) {
            seekAttempted = true;
            metadataAudio.ontimeupdate = () => {
              metadataAudio.ontimeupdate = null;
              const seeked = Number(metadataAudio.duration);
              try {
                metadataAudio.currentTime = 0;
              } catch (_) {}
              finalizeMetadata(seeked);
            };
            try {
              metadataAudio.currentTime = 1e9;
            } catch (_) {
              finalizeMetadata(metadataAudio.duration);
            }
          }
        };
        metadataAudio.onloadedmetadata = tryResolveMetadata;
        metadataAudio.ondurationchange = tryResolveMetadata;

        metadataAudio.onerror = () => {
          cleanupMetadataAudio();
          window.URL.revokeObjectURL(blobUrl);
          audioDurationLoadingRef.current.delete(msgId);
        };
        // The seek round-trip can take a moment on slower devices.
        fallbackTimer = setTimeout(() => {
          cleanupMetadataAudio();
          window.URL.revokeObjectURL(blobUrl);
          audioDurationLoadingRef.current.delete(msgId);
        }, 5000);
        metadataAudio.src = blobUrl;
        metadataAudio.load();
      } catch (error) {
        audioDurationLoadingRef.current.delete(msgId);
      }
    };

    messages.forEach((message) => {
      const msgType = message.message_type || message.type || message.messageType || 'text';
      const isAudioMessage =
        msgType === 'audio' ||
        (msgType === 'system' &&
          (message.fileType?.includes('audio') ||
            message.file_url?.includes('audio') ||
            message.file_name?.includes('audio') ||
            message.file_name?.endsWith('.webm')));

      if (!isAudioMessage || !message.file_url) return;

      const msgId = message.id || message.tempId;
      const audioFileUrl = getFileUrl(message.file_url);
      preloadAudioDuration(msgId, audioFileUrl);
    });
  }, [messages, decryptedToken]);

  // Audio playback function (following documentation)
  const toggleAudioPlayback = async (messageId, audioUrl) => {
    if (!audioUrl || audioUrl.trim() === '') {
      console.error('❌ Invalid audio URL:', audioUrl);
      return;
    }

    // If already playing this audio, pause it
    if (playingAudioId === messageId) {
      const audio = audioRefs.current[messageId];
      if (audio) {
        audio.pause();
        setPlayingAudioId(null);
        return;
      }
    }

    try {
      // Construct full URL if needed
      const fullAudioUrl = getFileUrl(audioUrl);

      // Fetch audio with authentication (handles CORS)
      const response = await fetch(fullAudioUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          'Accept': 'audio/webm, audio/*',
        },
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Store blob URL for cleanup
      audioBlobUrlsRef.current[messageId] = blobUrl;

      // Create audio element
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.src = blobUrl;
      audioRefs.current[messageId] = audio;

      // Load metadata (duration)
      let playbackMetadataResolved = false;
      const handleLoadedMetadata = () => {
        if (playbackMetadataResolved) return;
        const duration = isFinite(audio.duration) ? audio.duration : 0;
        if (duration <= 0) return;
        playbackMetadataResolved = true;
        setAudioProgressById((prev) => ({
          ...prev,
          [messageId]: { current: prev[messageId]?.current || 0, duration },
        }));
      };
      audio.onloadedmetadata = handleLoadedMetadata;
      audio.ondurationchange = handleLoadedMetadata;
      audio.oncanplaythrough = handleLoadedMetadata;

      // Track progress
      audio.ontimeupdate = () => {
        setAudioProgressById((prev) => ({
          ...prev,
          [messageId]: {
            current: audio.currentTime || 0,
            duration: audio.duration || prev[messageId]?.duration || 0,
          },
        }));
      };

      // Handle end
      audio.onended = () => {
        setPlayingAudioId(null);
        // Cleanup blob URL
        if (audioBlobUrlsRef.current[messageId]) {
          window.URL.revokeObjectURL(audioBlobUrlsRef.current[messageId]);
          delete audioBlobUrlsRef.current[messageId];
        }
        audioRefs.current[messageId] = null;
        setAudioProgressById((prev) => ({
          ...prev,
          [messageId]: { current: 0, duration: prev[messageId]?.duration || 0 },
        }));
      };

      // Handle errors
      audio.onerror = (e) => {
        setPlayingAudioId(null);
        console.error('❌ Error playing audio:', e);
        // toast.error('Error playing audio. The audio file may be corrupted or unsupported.');
        // Cleanup on error
        if (audioBlobUrlsRef.current[messageId]) {
          URL.revokeObjectURL(audioBlobUrlsRef.current[messageId]);
          delete audioBlobUrlsRef.current[messageId];
        }
        audioRefs.current[messageId] = null;
      };

      // Ensure metadata is requested before playback so duration is ready in UI.
      audio.load();

      // Play audio
      await audio.play();
      setPlayingAudioId(messageId);
    } catch (error) {
      console.error('❌ Error fetching/playing audio:', error);
      toast.error('Failed to load audio file. Please check the URL or try again.');
    }
  };

  // Send message (following documentation - use socket with fileUrl)
  const sendMessage = async () => {
    if (!selectedThread || (!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl && !uploadedAudioUrl)) {
      return;
    }

    setIsSendingMessage(true);
    const tempId = `temp_${Date.now()}`;

    try {
      // Determine message type and file info
      let messageType = 'text';
      let messageContent = newMessage.trim() || 'File shared';
      let fileUrl = null;
      let fileName = null;
      let fileType = null;

      if (uploadedAudioUrl) {
        messageType = 'audio';
        fileUrl = uploadedAudioUrl;
        fileName = 'audio.webm';
        fileType = 'audio/webm';
        messageContent = newMessage.trim() || 'Audio message';
      } else if (uploadedFileUrl) {
        // Handle PDF and other document files
        messageType = 'system';
        fileUrl = uploadedFileUrl;
        fileName = selectedFile?.name || 'document.pdf';
        fileType = selectedFile?.type || 'application/pdf';
        messageContent = newMessage.trim() || 'File shared';
        
        console.log('📤 Preparing to send file message:', {
          messageType,
          fileUrl,
          fileName,
          fileType,
          content: messageContent,
          uploadedFileUrl,
          selectedFile: selectedFile ? { name: selectedFile.name, type: selectedFile.type } : null,
        });

        // Validate that fileUrl exists
        if (!fileUrl) {
          throw new Error('File URL is missing. Please upload the file again.');
        }
      } else if (uploadedImageUrl) {
        messageType = 'image';
        fileUrl = uploadedImageUrl;
        messageContent = newMessage.trim() || 'Image shared';
      }

      // Check socket connection
      const socket = socketService.getSocket();
      const isSocketConnected = socketService.getConnectionStatus();

      if (isSocketConnected && socket) {
        // Send via socket (preferred method) — include both messageType and message_type for backend compatibility
        const messageData = {
          threadId: Number(selectedThread.id),
          content: messageContent,
          messageType: messageType,
          message_type: messageType,
          tempId: tempId,
        };

        // Add file information if available
        if (fileUrl) {
          messageData.fileUrl = fileUrl;
        }
        if (fileName) {
          messageData.fileName = fileName;
        }
        if (fileType) {
          messageData.fileType = fileType;
        }

        console.log('📤 Sending message via socket:', messageData);
        console.log('📤 File details:', { fileUrl, fileName, fileType, messageType });
        socket.emit('send_message', messageData);

        // Optimistic update
        const optimisticMessage = {
          id: tempId,
          tempId: tempId,
          thread_id: selectedThread.id,
          sender_id: userId,
          content: messageContent,
          message_type: messageType,
          file_url: fileUrl || null,
          file_name: fileName || null,
          file_type: fileType || null,
          created_at: new Date().toISOString(),
          delivery_status: 'sent',
        };

        console.log('📤 Optimistic message created:', optimisticMessage);
        if (messageType === 'audio') {
          const optimisticDuration =
            uploadedAudioDuration > 0 ? uploadedAudioDuration : recordingDuration > 0 ? recordingDuration : 0;
          if (optimisticDuration > 0) {
            setAudioProgressById((prev) => ({
              ...prev,
              [tempId]: { current: 0, duration: optimisticDuration },
            }));
          }
        }

        setMessages((prev) => {
          const updated = [...prev, optimisticMessage];
          return updated.sort((a, b) => {
            const aTime = new Date(a.created_at || a.createdAt || 0);
            const bTime = new Date(b.created_at || b.createdAt || 0);
            return aTime - bTime;
          });
        });

        updateThreadInList(selectedThread.id, {
          content: messageContent,
          message_type: messageType,
          sender_id: userId,
          created_at: optimisticMessage.created_at,
        });

        // Clear states
        setNewMessage('');
        setUploadedImageUrl(null);
        setUploadedFileUrl(null);
        setUploadedAudioUrl(null);
        setUploadedAudioDuration(0);
        setSelectedImageFile(null);
        setImagePreview(null);
        setSelectedFile(null);
        setAudioBlob(null);
        setAudioUrl(null);
      } else {
        // Fallback to HTTP API
        console.log('⚠️ Socket not connected, using HTTP API fallback');
        
        if (fileUrl) {
          // Send message with file via API (text, image, audio, or document) — include both messageType and message_type
          const response = await fetch(`${BASE_URL}/messages/threads/${selectedThread.id}/messages`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${decryptedToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              content: messageContent,
              messageType: messageType,
              message_type: messageType,
              ...(fileUrl && { fileUrl }),
              ...(fileName && { fileName }),
              ...(fileType && { fileType }),
            }),
          });

          const result = await response.json();
          if (result.error) {
            throw new Error(result.message);
          }

          if (result.data?.message) {
        setMessages((prev) => {
              const updated = [...prev, result.data.message];
          return updated.sort((a, b) => {
            const aTime = new Date(a.created_at || a.createdAt || 0);
            const bTime = new Date(b.created_at || b.createdAt || 0);
                return aTime - bTime;
          });
        });
          }
        } else {
          // Send text message via API
        const response = await messagingApi.sendMessage(
          selectedThread.id,
            messageContent,
          'text'
        );

        if (response.error) {
          throw new Error(response.message);
        }

        if (response.data?.message) {
          setMessages((prev) => {
            const filtered = prev.filter((msg) => msg.id !== tempId && msg.tempId !== tempId);
            const updated = [...filtered, response.data.message];
            return updated.sort((a, b) => {
              const aTime = new Date(a.created_at || a.createdAt || 0);
              const bTime = new Date(b.created_at || b.createdAt || 0);
                return aTime - bTime;
            });
          });
        }
        }

        // Clear states
        setNewMessage('');
        setUploadedImageUrl(null);
        setUploadedFileUrl(null);
        setUploadedAudioUrl(null);
        setUploadedAudioDuration(0);
        setSelectedImageFile(null);
        setImagePreview(null);
        setSelectedFile(null);
        setAudioBlob(null);
        setAudioUrl(null);

        // Update thread list
        updateThreadInList(selectedThread.id, {
          content: messageContent,
          message_type: messageType,
          sender_id: userId,
          created_at: new Date().toISOString(),
        });
      }

      scrollToBottom();
    } catch (error) {
      console.error('❌ Error sending message:', error);
      toast.error(error.message || 'Failed to send message');
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId && msg.tempId !== tempId));
    } finally {
      setIsSendingMessage(false);
    }
  };

  // Handle file selection (following documentation - upload first, then send)
  const handleFileSelect = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isDocument = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ].includes(file.type);

    // Validate file type
    if (!isImage && !isDocument) {
      toast.error('Please select an image, PDF, or Word document');
      return;
    }

    // Validate file size (5MB for images, 10MB for documents)
    const maxSize = isImage ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(isImage ? 'Image size should be less than 5MB' : 'File size should be less than 10MB');
      return;
    }

    if (isImage) {
      // Handle image upload
        setSelectedImageFile(file);

      // Create preview
        const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);

      // Auto-upload image
      setIsUploadingImage(true);
      try {
        const imageUrl = await uploadImage(file);
        setUploadedImageUrl(imageUrl);
        console.log('✅ Image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('❌ Image upload failed:', error);
        toast.error('Image upload failed. Please try again.');
        setImagePreview(null);
        setSelectedImageFile(null);
      } finally {
        setIsUploadingImage(false);
        event.target.value = ''; // Reset input
      }
    } else {
      // Handle document upload (PDF, Word, Excel, etc.)
      console.log('📄 Uploading document file:', { name: file.name, type: file.type, size: file.size });
      setSelectedFile(file);
      setIsUploadingFile(true);
      try {
        const fileUrl = await uploadFile(file);
        setUploadedFileUrl(fileUrl);
        console.log('✅ File uploaded successfully. URL:', fileUrl);
        console.log('✅ File details:', { name: file.name, type: file.type, url: fileUrl });
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error('❌ File upload failed:', error);
        toast.error('File upload failed. Please try again.');
        setSelectedFile(null);
        setUploadedFileUrl(null);
      } finally {
        setIsUploadingFile(false);
        event.target.value = ''; // Reset input
      }
    }
  };

  // Handle audio recording (following documentation)
  const startRecording = async () => {
    try {
      // Check if MediaRecorder is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Your browser does not support audio recording. Please use a modern browser.');
        return;
      }

      if (!MediaRecorder.isTypeSupported('audio/webm')) {
        console.warn('audio/webm not supported, trying default codec');
      }

      // Request microphone access with proper error handling
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });
      } catch (permissionError) {
        console.error('❌ Permission error:', permissionError);
        
        // Handle different error types
        if (permissionError.name === 'NotAllowedError' || permissionError.name === 'PermissionDeniedError') {
          toast.error('Microphone permission denied. Please allow microphone access in your browser settings and try again.', {
            duration: 5000,
          });
        } else if (permissionError.name === 'NotFoundError' || permissionError.name === 'DevicesNotFoundError') {
          toast.error('No microphone found. Please connect a microphone and try again.');
        } else if (permissionError.name === 'NotReadableError' || permissionError.name === 'TrackStartError') {
          toast.error('Microphone is being used by another application. Please close other applications and try again.');
        } else {
          toast.error(`Microphone access error: ${permissionError.message || 'Unknown error'}. Please check your browser settings.`);
        }
        return;
      }

      // Check if MediaRecorder is supported
      if (!MediaRecorder) {
        toast.error('MediaRecorder API is not supported in your browser.');
        // Clean up stream if MediaRecorder is not available
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      // Create MediaRecorder with the stream
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      // Store stream reference separately for cleanup (stream is read-only on MediaRecorder)
      mediaStreamRef.current = stream;
      audioChunksRef.current = [];

      // Collect audio chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording errors
      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        toast.error('Recording error occurred. Please try again.');
        stopRecording();
      };

      // Handle recording stop
      mediaRecorder.onstop = async () => {
        // Create blob from chunks
        if (audioChunksRef.current.length === 0) {
          console.warn('No audio chunks recorded');
          toast.error('No audio was recorded. Please try again.');
          return;
        }

        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = window.URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioUrl(url);
        const extractedDuration = await getAudioDurationFromBlob(audioBlob);
        setUploadedAudioDuration(extractedDuration > 0 ? extractedDuration : recordingDuration || 0);

        // Auto-upload audio (following documentation)
        setIsUploadingAudio(true);
        try {
          const audioFileUrl = await uploadAudio(audioBlob);
          setUploadedAudioUrl(audioFileUrl);
          console.log('✅ Audio uploaded successfully:', audioFileUrl);
        } catch (error) {
          console.error('❌ Audio upload failed:', error);
          toast.error('Audio upload failed. Please try again.');
        } finally {
          setIsUploadingAudio(false);
        }
      };

      // Start recording
      try {
        mediaRecorder.start(1000); // Collect data every 1 second
        setIsRecording(true);
        setRecordingDuration(0);
        setUploadedAudioDuration(0);

        // Start timer
        recordingTimerRef.current = setInterval(() => {
          setRecordingDuration((prev) => prev + 1);
        }, 1000);
      } catch (startError) {
        console.error('❌ Error starting MediaRecorder:', startError);
        toast.error('Failed to start recording. Please try again.');
        stream.getTracks().forEach(track => track.stop());
      }
    } catch (error) {
      console.error('❌ Unexpected error starting recording:', error);
      toast.error(`Recording error: ${error.message || 'Unknown error'}. Please try again.`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      // Stop all tracks from the stream (using separate ref)
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      setIsRecording(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
  };

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Format message time
  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get participant info
  const getParticipantInfo = (thread) => {
    if (!thread) return { id: null, name: 'Unknown', email: '', role: '', image: null, is_online: false };
    
    // Try multiple ways to identify the current user
    const currentUserId = userId;
    const isParticipantOne = 
      thread.participant_one_id === currentUserId || 
      String(thread.participant_one_id) === String(currentUserId);
    
    let participantId, participantName, participantEmail, participantRole;

    if (isParticipantOne) {
      participantId = thread.participant_two_id || thread.other_user_id;
      participantName = thread.participant_two_name || thread.other_user_name || 'Unknown User';
      participantEmail = thread.participant_two_email || thread.other_user_email || '';
      participantRole = thread.participant_two_role || thread.other_user_role || '';
    } else {
      participantId = thread.participant_one_id || thread.other_user_id;
      participantName = thread.participant_one_name || thread.other_user_name || 'Unknown User';
      participantEmail = thread.participant_one_email || thread.other_user_email || '';
      participantRole = thread.participant_one_role || thread.other_user_role || '';
    }

    const participantImage =
      resolveParticipantAvatarRaw(thread, isParticipantOne, workerAvatarById) || null;
    
    // Get online status from userStatuses state
    const isOnline = participantId ? (userStatuses[participantId]?.isOnline || false) : false;
    
    return {
      id: participantId,
      name: participantName,
      email: participantEmail,
      role: participantRole,
      image: participantImage,
      is_online: isOnline,
    };
  };

  // Format last message content
  const formatLastMessage = (lastMessage) => {
    if (!lastMessage) return t('No messages') || 'No messages';
    
    // Handle if lastMessage is a string (some APIs return just the content)
    if (typeof lastMessage === 'string') {
      return lastMessage.length > 30 ? lastMessage.substring(0, 30) + '...' : lastMessage;
    }
    
    // Handle different message type field names
    const messageType = lastMessage.message_type || lastMessage.type || lastMessage.messageType || 'text';
    const content = lastMessage.content || lastMessage.msg || lastMessage.text || '';
    
    if (messageType === 'image') {
      return '📷 Image';
    } else if (messageType === 'pdf' || messageType === 'document' || messageType === 'file') {
      return '📄 Document';
    } else if (messageType === 'audio') {
      return '🎤 Audio';
    } else if (content) {
      // Remove "File shared" placeholder and show actual content
      const trimmedContent = content.trim();
      if (trimmedContent === 'File shared' || trimmedContent === 'Image shared' || trimmedContent === 'Audio message') {
        if (messageType === 'image') return '📷 Image';
        if (messageType === 'pdf' || messageType === 'document' || messageType === 'file') return '📄 Document';
        if (messageType === 'audio') return '🎤 Audio';
      }
      return trimmedContent.length > 30 ? trimmedContent.substring(0, 30) + '...' : trimmedContent;
    }
    return t('No messages') || 'No messages';
  };

  // Keep composer modes mutually exclusive: text OR media.
  const hasTypedText = Boolean(newMessage.trim());
  const hasImageOrFileSelected = Boolean(uploadedImageUrl || uploadedFileUrl || selectedImageFile || selectedFile);
  const hasAudioSelected = Boolean(uploadedAudioUrl || audioBlob);
  const hasAnyMediaSelected = hasImageOrFileSelected || hasAudioSelected;
  const isComposerBusy = isSendingMessage || isUploadingImage || isUploadingFile || isUploadingAudio;

  const selectedParticipantForHeader = selectedThread ? getParticipantInfo(selectedThread) : null;

  return (
    <Box sx={{ px: 1, mt: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          height: { xs: 'auto', md: '80vh' },
          border: '2px solid #091E4224',
          borderRadius: 2,
          backgroundColor: 'white',
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            width: { xs: '100%', md: 380 },
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <CustomText
                sx={{
                  fontWeight: 500,
                  fontSize: '22px',
                  color: '#2C384C',
                  fontFamily: 'Poppins, sans-serif',
                
                }}
              >
                {/* {t('Messages')}
                 */}
                 {t('messages_title')}
              </CustomText>
            </Box>

            <IconButton
              sx={{ backgroundColor: '#003149', p: 0.2, borderRadius: '50%' }}
              onClick={() => {
                setShowChatTypeDialog(true);
              }}
            >
              <Add sx={{ color: 'white' }} />
            </IconButton>
          </Box>

          <Divider />

          {/* Search */}
          <Box sx={{ px: 1, py: 0.5 }}>
            <Box
              sx={{
                px: 0.5,
                mt: 0.5,
                py: 0.4,
                borderRadius: 1,
                height: 34,
                border: '2px solid #091E4224',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <InputBase
                placeholder={t('search_placeholder') || 'Search...'}
                value={threadSearchTerm}
                onChange={(e) => setThreadSearchTerm(e.target.value)}
                sx={{
                  ml: 0.5,
                  fontSize: 15,
                  color: '#939393',
                  fontWeight: 500,
                }}
              />
              <SearchIcon sx={{ fontSize: 22, color: '#888', mr: 0.5 }} />
            </Box>
          </Box>

          {/* Threads List */}
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
            {isLoadingThreads ? (
              Array.from({ length: 5 }).map((_, index) => (
                <ListItem key={index} sx={{ px: 1, py: 1 }}>
                  <ListItemAvatar>
                    <Skeleton variant="circular" width={40} height={40} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={<Skeleton variant="text" width="60%" />}
                    secondary={<Skeleton variant="text" width="40%" />}
                  />
                </ListItem>
              ))
            ) : threads.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <img src={chatimage} alt="chat" style={{ width: '150px', marginBottom: '20px' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('No conversations yet') || 'No conversations yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  {t('Click the + button to start a new chat') || 'Click the + button to start a new chat'}
                </Typography>
              </Box>
            ) : (
              <List>
                {threads
                  .filter((thread) => {
                    const participant = getParticipantInfo(thread);
                    const q = threadSearchTerm.toLowerCase();
                    const name = (participant.name || '').toLowerCase();
                    const email = (participant.email || '').toLowerCase();
                    return name.includes(q) || email.includes(q);
                  })
                  .map((thread) => {
                    const participant = getParticipantInfo(thread);
                    const isSelected = selectedThread?.id === thread.id;

                    return (
                      <ListItem
                        key={thread.id}
                        button
                        selected={isSelected}
                        onClick={() => handleThreadSelect(thread)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          borderRadius: 2,
                          backgroundColor:
                            isSelected ? '#F0F9FF' : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected ? '#F0F9FF' : '#f5f5f5',
                          },
                          position: 'relative',
                          '&::before': isSelected
                            ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                height: '50%',
                                width: '4px',
                                backgroundColor: '#006EC2',
                                borderRadius: '2px',
                              }
                            : {},
                        }}
                      >
                        <ListItemAvatar sx={{ flexShrink: 0 }}>
                          <Box sx={{ position: 'relative', display: 'inline-block' }}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              variant="dot"
                              sx={{
                                '& .MuiBadge-badge': {
                                  backgroundColor: participant.is_online ? '#4CAF50' : '#ccc',
                                  border: '2px solid white',
                                  width: 12,
                                  height: 12,
                                  zIndex: 1,
                                },
                              }}
                            >
                              <Avatar
                                src={participant.image || undefined}
                                alt={participant.name}
                                sx={{ width: 40, height: 40 }}
                                slotProps={avatarSlotPropsForSrc(participant.image)}
                              >
                                {participant.name?.charAt(0)?.toUpperCase() || 'U'}
                              </Avatar>
                            </Badge>
                          </Box>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ flex: '1 1 auto', minWidth: 0 }}
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography
                                sx={{
                                  fontWeight: '600',
                                  fontSize: '14px',
                                  color: isSelected ? '#006EC2' : '#100F0F',
                                  fontFamily: 'Poppins, sans-serif',
                                }}
                              >
                                {participant.name || 'Unknown User'}
                              </Typography>
                              {participant.role && (
                                <Chip
                                  label={getRoleBadgeLabel(participant.role, t)}
                                  size="small"
                                  sx={{
                                    backgroundColor: getRoleBadgeColor(participant.role),
                                    color: getRoleBadgeTextColor(participant.role),
                                    fontWeight: 600,
                                    fontFamily: 'Poppins, sans-serif',
                                    fontSize: '10px',
                                    height: '20px',
                                  }}
                                  clickable={false}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              {participant.email && (
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    color: isSelected ? '#006EC2' : '#666666',
                                    fontFamily: 'Poppins, sans-serif',
                                    fontWeight: isSelected ? '500' : '400',
                                    mb: 0.5,
                                  }}
                                >
                                  {participant.email}
                                </Typography>
                              )}
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: isSelected ? '#006EC2' : '#666666',
                                  fontFamily: 'Poppins, sans-serif',
                                  fontWeight: isSelected ? '500' : '400',
                                }}
                              >
                                {formatLastMessage(thread.last_message)}
                              </Typography>
                            </Box>
                          }
                        />
                        <Box
                          sx={{
                            flexShrink: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            ml: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '12px',
                              fontFamily: 'Poppins, sans-serif',
                              color: isSelected ? '#006EC2' : '#666666',
                              fontWeight: isSelected ? '500' : '400',
                            }}
                          >
                            {thread.last_message?.created_at || thread.last_message_at || thread.updated_at
                              ? formatMessageTime(thread.last_message?.created_at || thread.last_message_at || thread.updated_at)
                              : ''}
                          </Typography>
                          {((thread.unread_count ?? thread.unreadCount) || 0) > 0 && (
                            <Box
                              sx={{
                                backgroundColor: '#006EC2',
                                color: 'white',
                                borderRadius: '50%',
                                minWidth: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 600,
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            >
                              {thread.unread_count ?? thread.unreadCount ?? 0}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
              </List>
            )}
          </Box>
        </Box>

        {/* Right side - Chat Window */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedThread ? (
            <>
              {/* Chat Header */}
              <Box
                sx={{
                  px: 3,
                  py: 1.2,
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ position: 'relative', mr: 2 }}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: selectedParticipantForHeader?.is_online ? '#4CAF50' : '#ccc',
                          border: '2px solid white',
                          width: 12,
                          height: 12,
                        },
                      }}
                    >
                      <Avatar
                        src={selectedParticipantForHeader?.image || undefined}
                        alt={selectedParticipantForHeader?.name || ''}
                        sx={{ width: 44, height: 44 }}
                        slotProps={avatarSlotPropsForSrc(selectedParticipantForHeader?.image)}
                      >
                        {selectedParticipantForHeader?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                    </Badge>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: '600',
                        fontSize: '16px',
                        fontFamily: 'Poppins, sans-serif',
                       
                      }}
                    >
                      {selectedParticipantForHeader?.name || 'Unknown User'}
                    </Typography>
                    {selectedParticipantForHeader?.email ? (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#666',
                          fontFamily: 'Poppins, sans-serif',
                          display: 'block',
                          wordBreak: 'break-all',
                        }}
                      >
                        {selectedParticipantForHeader.email}
                      </Typography>
                    ) : null}
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#666',
                        fontFamily: 'Poppins, sans-serif',
                       
                      }}
                    >
                      {selectedParticipantForHeader?.is_online
                        ? (t('Online') || 'Online')
                        : (selectedParticipantForHeader?.role === 'worker'
                            ? (t('employee') || 'Employee')
                            : getRoleBadgeLabel(selectedParticipantForHeader?.role, t))}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectedThread && selectedParticipantForHeader?.role === 'worker' && (
                    <IconButton
                      onClick={() => {
                        setShowBroadcastDialog(true);
                        loadBroadcastWorkers();
                      }}
                      title="Broadcast Message"
                      sx={{ color: '#006EC2' }}
                    >
                      <BroadcastOnPersonal />
                    </IconButton>
                  )}
                  <IconButton onClick={() => setSelectedThread(null)}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>

              {/* Messages Area */}
              <Box
                sx={{
                  flexGrow: 1,
                  px: 3,
                  py: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: 'calc(80vh - 100px)',
                }}
              >
                {isLoadingMessages ? (
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <CircularProgress size={40} />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#888',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('loading_messages') || 'Loading messages...'}
                    </Typography>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#888',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('No messages yet') || 'No messages yet'}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: '#aaa',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('Start the conversation') || 'Start the conversation'}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Top: Load older messages or beginning of conversation */}
                    {hasMoreMessages ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <Typography
                          component="button"
                          type="button"
                          onClick={loadMoreMessages}
                          disabled={isLoadingMoreMessages}
                          sx={{
                            fontSize: '13px',
                            color: '#006EC2',
                            fontWeight: 600,
                            cursor: isLoadingMoreMessages ? 'default' : 'pointer',
                            border: 'none',
                            background: 'none',
                            fontFamily: 'Poppins, sans-serif',
                            textDecoration: 'underline',
                            '&:hover': isLoadingMoreMessages ? {} : { color: '#0056b3' },
                          }}
                        >
                          {isLoadingMoreMessages
                            ? (t('Loading...') || 'Loading...')
                            : (t('Load older messages') || 'Load older messages')}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#aaa',
                            fontFamily: 'Poppins, sans-serif',
                          }}
                        >
                          {t('beginningOfConversation') || 'Beginning of conversation'}
                        </Typography>
                      </Box>
                    )}
                    {messages.map((message) => {
                    const isMine = message.sender_id === userId;
                    return (
                      <Box
                        key={message.id || `temp-${message.tempId || Date.now()}`}
                        sx={{
                          alignSelf: isMine ? 'flex-end' : 'flex-start',
                          backgroundColor: isMine ? '#006EC2' : '#F0F9FF',
                          color: isMine ? 'white' : '#2C3E50',
                          borderRadius: 2,
                          px: 2,
                          py: 0.5,
                          maxWidth: '70%',
                        }}
                      >
                          {message.message_type === 'text' && message.content && (
                            <Typography
                              sx={{
                                fontSize: '13px',
                                fontWeight: 400,
                                fontFamily: 'Poppins, sans-serif',
                                color: isMine ? 'white' : '#2C3E50',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {message.content}
                            </Typography>
                          )}

                          {message.message_type === 'image' && message.file_url && (
                            <Box>
                              <img
                                crossOrigin="anonymous"
                                src={getFileUrl(message.file_url)}
                                alt={message.file_name || 'Shared image'}
                                style={{
                                  width: '120px',
                                  height: '120px',
                                  borderRadius: '8px',
                                  objectFit: 'cover',
                                  cursor: 'pointer',
                                  display: 'block',
                                  marginBottom:
                                    message.content &&
                                    message.content !== 'Image shared' &&
                                    message.content !== 'File shared' &&
                                    message.content !== 'Image'
                                      ? '8px'
                                      : 0,
                                }}
                                onClick={() => {
                                  const imageUrl = getFileUrl(message.file_url);
                                  if (imageUrl) window.open(imageUrl, '_blank');
                                }}
                              />
                              {message.content &&
                                message.content !== 'Image shared' &&
                                message.content !== 'File shared' &&
                                message.content !== 'Image' && (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    fontFamily: 'Poppins, sans-serif',
                                    color: isMine ? 'white' : '#2C3E50',
                                    mb: 1,
                                  }}
                                >
                                  {message.content}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* PDF/Document message rendering (message_type: 'system' for files) */}
                          {message.message_type === 'system' &&
                            !(
                              message.fileType?.includes('audio') ||
                              message.file_url?.includes('audio') ||
                              message.file_name?.includes('audio') ||
                              message.file_name?.endsWith('.webm')
                            ) &&
                            message.file_url && (
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  p: 1.5,
                                  backgroundColor: isMine ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
                                  borderRadius: '8px',
                                  border: isMine ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e0e0e0',
                                  '&:hover': {
                                    backgroundColor: isMine ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    backgroundColor: isMine ? 'rgba(255, 255, 255, 0.2)' : '#006EC2',
                                  }}
                                >
                                  <Typography sx={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
                                    PDF
                                  </Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      fontFamily: 'Poppins, sans-serif',
                                      color: isMine ? 'white' : '#2C3E50',
                                      mb: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {message.file_name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '11px',
                                      fontFamily: 'Poppins, sans-serif',
                                      color: isMine ? 'rgba(255, 255, 255, 0.7)' : '#666',
                                      textTransform: 'uppercase',
                                    }}
                                  >
                                    PDF Document
                                  </Typography>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const fileUrl = getFileUrl(message.file_url);
                                    if (fileUrl) window.open(fileUrl, '_blank');
                                  }}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    backgroundColor: isMine ? 'rgba(255, 255, 255, 0.2)' : '#006EC2',
                                    color: 'white',
                                    '&:hover': {
                                      backgroundColor: isMine ? 'rgba(255, 255, 255, 0.3)' : '#0056b3',
                                    },
                                  }}
                                >
                                  <Download sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                              {message.content && (
                                <Typography
                                  sx={{
                                    fontSize: '13px',
                                    fontWeight: 400,
                                    fontFamily: 'Poppins, sans-serif',
                                    color: isMine ? 'white' : '#2C3E50',
                                    mb: 1,
                                  }}
                                >
                                  {message.content !== 'File shared' ? message.content : ''}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Audio message rendering: message_type 'audio' (sent/received) or legacy 'system' with audio file */}
                          {(() => {
                            const msgType = message.message_type || message.type || message.messageType || 'text';
                            const isAudioMessage =
                              msgType === 'audio' ||
                              (msgType === 'system' &&
                                (message.fileType?.includes('audio') ||
                                  message.file_url?.includes('audio') ||
                                  message.file_name?.includes('audio') ||
                                  message.file_name?.endsWith('.webm')));

                            if (isAudioMessage && message.file_url) {
                              const audioFileUrl = getFileUrl(message.file_url);
                              const msgId = message.id || message.tempId;
                              const progress = audioProgressById[msgId] || { current: 0, duration: 0 };
                              const percent = progress.duration > 0 ? (progress.current / progress.duration) * 100 : 0;

                              return (
                            <Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.25,
                                      p: 1.25,
                                      backgroundColor: isMine ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
                                      borderRadius: '14px',
                                      minWidth: '240px',
                                      maxWidth: '360px',
                                    }}
                                  >
                                    {/* Play/Pause Button */}
                                    <IconButton
                                      onClick={() => toggleAudioPlayback(msgId, audioFileUrl)}
                                      sx={{
                                        width: 38,
                                        height: 38,
                                        backgroundColor: playingAudioId === msgId ? '#005bb0' : '#006EC2',
                                        color: 'white',
                                        borderRadius: '50%',
                                        '&:hover': {
                                          backgroundColor: playingAudioId === msgId ? '#004a8f' : '#0056b3',
                                        },
                                      }}
                                    >
                                      {playingAudioId === msgId ? <Pause /> : <PlayArrow />}
                                    </IconButton>

                                    {/* Progress Bar */}
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box
                                        sx={{
                                          position: 'relative',
                                          flex: 1,
                                          height: 6,
                                          borderRadius: 999,
                                          backgroundColor: isMine ? 'rgba(255, 255, 255, 0.3)' : '#E1E4E8',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            height: 6,
                                            width: `${percent}%`,
                                            backgroundColor: isMine ? 'white' : '#006EC2',
                                            borderRadius: 999,
                                          }}
                                        />
                                      </Box>

                                      {/* Time Display */}
                                      <Typography
                                        sx={{
                                          fontSize: '11px',
                                          minWidth: 68,
                                          color: isMine ? 'rgba(255, 255, 255, 0.9)' : '#666',
                                          fontFamily: 'Poppins, sans-serif',
                                        }}
                                      >
                                        {formatDuration(progress.current)} / {formatDuration(progress.duration)}
                                      </Typography>

                                      {/* Download Button */}
                                      <IconButton
                                        component="a"
                                        href={audioFileUrl}
                                        download
                                        sx={{
                                          width: 28,
                                          height: 28,
                                          color: isMine ? 'rgba(255, 255, 255, 0.9)' : '#666',
                                          '&:hover': {
                                            backgroundColor: isMine ? 'rgba(255, 255, 255, 0.2)' : '#e0e0e0',
                                          },
                                        }}
                                      >
                                        <Download sx={{ fontSize: 14 }} />
                                      </IconButton>
                                    </Box>
                                  </Box>
                                  {message.content && message.content !== 'Audio message' && message.content !== 'File shared' && (
                                    <Typography
                                      sx={{
                                        fontSize: '13px',
                                        fontWeight: 400,
                                        fontFamily: 'Poppins, sans-serif',
                                        color: isMine ? 'white' : '#2C3E50',
                                        mt: 1,
                                      }}
                                    >
                                  {message.content}
                                </Typography>
                              )}
                            </Box>
                              );
                            }
                            return null;
                          })()}

                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <Typography
                              sx={{
                                fontSize: '10px',
                                fontWeight: 400,
                                color: isMine ? 'rgba(255, 255, 255, 0.8)' : '#666666',
                                fontFamily: 'Poppins, sans-serif',
                              }}
                            >
                              {formatMessageTime(message.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Image Preview */}
              {imagePreview && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderTop: '1px solid #E8F4FD',
                    backgroundColor: '#F8F9FA',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      border: '1px solid #E8F4FD',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ImageIcon sx={{ color: '#006EC2', fontSize: 20 }} />
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: '#666',
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {isUploadingImage
                          ? t('Uploading image...') || 'Uploading image...'
                          : uploadedImageUrl
                          ? t('Image ready to send') || 'Image ready to send'
                          : t('Image ready to send') || 'Image ready to send'}
                      </Typography>
                      {isUploadingImage && (
                        <CircularProgress size={16} sx={{ color: '#006EC2', ml: 1 }} />
                      )}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setImagePreview(null);
                        setSelectedImageFile(null);
                        setUploadedImageUrl(null);
                      }}
                      disabled={isUploadingImage}
                      sx={{ color: '#666', '&:disabled': { opacity: 0.5 } }}
                    >
                      <Close />
                    </IconButton>
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '8px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </Box>
                </Box>
              )}

              {/* File Upload Status */}
              {selectedFile && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderTop: '1px solid #E8F4FD',
                    backgroundColor: '#F8F9FA',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '18px',
                      backgroundColor: '#F0F2F5',
                      border: '1px solid #E3E6EA',
                    }}
                  >
                    <Description sx={{ fontSize: 18, color: '#667781' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: '12.5px',
                          fontFamily: 'Poppins, sans-serif',
                          color: '#111B21',
                          fontWeight: 600,
                        }}
                      >
                        {isUploadingFile
                          ? t('Uploading file...') || 'Uploading file...'
                          : uploadedFileUrl
                          ? t('File ready to send') || 'File ready to send'
                          : t('File ready') || 'File ready'}
                      </Typography>
                      {selectedFile.name && (
                        <Typography
                          sx={{
                            fontSize: '11px',
                            fontFamily: 'Poppins, sans-serif',
                            color: '#667781',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {selectedFile.name}
                        </Typography>
                      )}
                    </Box>
                    {isUploadingFile && (
                      <CircularProgress size={16} sx={{ color: '#667781' }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedFile(null);
                        setUploadedFileUrl(null);
                      }}
                      disabled={isUploadingFile}
                      sx={{
                        color: '#667781',
                        p: 0.5,
                        ml: 0.5,
                        '&:hover': { backgroundColor: '#E8EEF3' },
                        '&:disabled': { opacity: 0.5 },
                      }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Audio Preview */}
              {audioUrl && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderTop: '1px solid #E8F4FD',
                    backgroundColor: '#F8F9FA',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '18px',
                      backgroundColor: '#F0F2F5',
                      border: '1px solid #E3E6EA',
                    }}
                  >
                    <Mic sx={{ fontSize: 18, color: '#667781' }} />
                    <Typography
                      sx={{
                        fontSize: '12.5px',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#111B21',
                        fontWeight: 600,
                      }}
                    >
                      {isUploadingAudio
                        ? t('Uploading audio...') || 'Uploading audio...'
                        : uploadedAudioUrl
                        ? t('Audio ready') || 'Audio ready'
                        : t('Audio ready') || 'Audio ready'}
                    </Typography>
                    {isUploadingAudio && (
                      <CircularProgress size={16} sx={{ color: '#667781' }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAudioUrl(null);
                        setAudioBlob(null);
                        setUploadedAudioUrl(null);
                        setUploadedAudioDuration(0);
                      }}
                      disabled={isUploadingAudio}
                      sx={{
                        color: '#667781',
                        p: 0.5,
                        ml: 0.5,
                        '&:hover': { backgroundColor: '#E8EEF3' },
                        '&:disabled': { opacity: 0.5 },
                      }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Message Input */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  borderTop: '1px solid #E8F4FD',
                  backgroundColor: '#FAFBFC',
                }}
              >
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, 'image')}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e, 'file')}
                />

                {!isRecording ? (
                  <>
                    {/* Mic button for audio recording */}
                    {!uploadedAudioUrl && (
                      <IconButton
                        onClick={startRecording}
                        disabled={
                          isComposerBusy ||
                          hasTypedText ||
                          hasImageOrFileSelected ||
                          hasAudioSelected
                        }
                        sx={{
                          mr: 1,
                          color: '#006EC2',
                          '&:hover': { backgroundColor: '#E8F4FD' },
                          '&:disabled': { color: '#ccc' },
                        }}
                      >
                        <Mic sx={{ fontSize: 24 }} />
                      </IconButton>
                    )}
                    {/* File picker button */}
                    <label htmlFor="file-upload">
                      <IconButton
                        component="span"
                        disabled={
                          isComposerBusy ||
                          isRecording ||
                          hasTypedText ||
                          hasAnyMediaSelected
                        }
                        sx={{
                          mr: 1,
                          color: '#006EC2',
                          '&:hover': { backgroundColor: '#E8F4FD' },
                          '&:disabled': { color: '#ccc' },
                        }}
                      >
                        <AttachFile sx={{ fontSize: 22 }} />
                      </IconButton>
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.type.startsWith('image/')) {
                            handleFileSelect(e, 'image');
                          } else {
                            handleFileSelect(e, 'file');
                          }
                        }
                        // Reset input to allow selecting same file again
                        e.target.value = '';
                      }}
                      style={{ display: 'none' }}
                      id="file-upload"
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      gap: 1,
                      backgroundColor: '#F0F2F5',
                      borderRadius: '24px',
                      px: 1,
                      py: 0.5,
                      border: '1px solid #E3E6EA',
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: '#ff3b30',
                        mx: 1,
                        '@keyframes recPulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                        },
                        animation: 'recPulse 1.2s ease-in-out infinite',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        fontFamily: 'Poppins, sans-serif',
                        color: '#111B21',
                        minWidth: '48px',
                      }}
                    >
                      {recordingDuration > 0
                        ? `${Math.floor(recordingDuration / 60)}:${String(recordingDuration % 60).padStart(2, '0')}`
                        : '0:00'}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      onClick={stopRecording}
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor: '#25D366',
                        color: 'white',
                        mr: 0.5,
                        '&:hover': { backgroundColor: '#20bd5a' },
                      }}
                    >
                      <Stop sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        stopRecording();
                        setAudioBlob(null);
                        setAudioUrl(null);
                      }}
                      sx={{
                        width: 36,
                        height: 36,
                        color: '#667781',
                        '&:hover': { backgroundColor: '#E8EEF3' },
                      }}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                )}

                {!isRecording && (
                  <>
                    <InputBase
                      placeholder={t('type_message_placeholder') || 'Type a message...'}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      disabled={hasAnyMediaSelected || isComposerBusy}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      sx={{
                        flexGrow: 1,
                        border: '1px solid #E8F4FD',
                        borderRadius: '24px',
                        px: 2,
                        py: 1.2,
                        height: '42px',
                        fontSize: '14px',
                        fontFamily: 'Poppins, sans-serif',
                        backgroundColor: '#FFFFFF',
                        '&:focus': {
                          borderColor: '#006EC2',
                          outline: 'none',
                        },
                        '&::placeholder': {
                          color: '#7F8C8D',
                        },
                      }}
                    />

                    <IconButton
                      onClick={sendMessage}
                      disabled={
                        isComposerBusy ||
                        (!newMessage.trim() && !uploadedImageUrl && !uploadedFileUrl && !uploadedAudioUrl)
                      }
                      sx={{
                        ml: 1,
                        backgroundColor:
                          !newMessage.trim() && !selectedImageFile && !selectedFile && !audioBlob
                            ? '#E8F4FD'
                            : '#006EC2',
                        color:
                          !newMessage.trim() && !selectedImageFile && !selectedFile && !audioBlob
                            ? '#006EC2'
                            : 'white',
                        '&:hover': {
                          backgroundColor:
                            !newMessage.trim() && !selectedImageFile && !selectedFile && !audioBlob
                              ? '#E8F4FD'
                              : '#0056b3',
                        },
                        '&:disabled': {
                          backgroundColor: '#E8F4FD',
                          color: '#ccc',
                        },
                        width: 40,
                        height: 40,
                      }}
                    >
                      {isSendingMessage ? (
                        <CircularProgress size={20} sx={{ color: '#006EC2' }} />
                      ) : (
                        <Send sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </>
                )}
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                color: '#5C5C5C',
              }}
            >
              <img src={chatimage} alt="chat" style={{ width: '200px', marginBottom: '20px' }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '18px',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('selectConversation') || 'Select a conversation'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#888',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {t('chooseConversation') || 'Choose a conversation to start chatting'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Chat Type Selection Dialog */}
      <Dialog
        open={showChatTypeDialog}
        onClose={() => setShowChatTypeDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              fontFamily: 'Poppins, sans-serif',
              color: '#2C384C',
            }}
          >
            {t('Select Chat Type') || 'Select Chat Type'}
          </Typography>
          <IconButton
            onClick={() => setShowChatTypeDialog(false)}
            size="small"
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2 }}>
            <List>
              <ListItem
                button
                onClick={() => {
                  handleChatTypeSelect('super_admin');
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  border: '1px solid #E3F2FD',
                  backgroundColor: '#F5FAFF',
                  '&:hover': { backgroundColor: '#E3F2FD' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#1976D2',
                      }}
                    >
                      {t('Super Admin') || 'Super Admin'}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: '#666',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('Select a Super Admin to chat with') || 'Select a Super Admin to chat with'}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleChatTypeSelect('account_executive');
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  border: '1px solid #F3E5F5',
                  backgroundColor: '#FBF5FF',
                  '&:hover': { backgroundColor: '#F3E5F5' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#7B1FA2',
                      }}
                    >
                      {t('Account Executive') || 'Account Executive'}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: '#666',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('Select an Account Executive to chat with') || 'Select an Account Executive to chat with'}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleChatTypeSelect('worker');
                }}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  border: '1px solid #E8F5E9',
                  backgroundColor: '#F1F8F4',
                  '&:hover': { backgroundColor: '#E8F5E9' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        fontFamily: 'Poppins, sans-serif',
                        color: '#388E3C',
                      }}
                    >
                      {t('EMPLOYEE') || 'Employee'}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: '#666',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {t('Select a Worker to chat with') || 'Select a Worker to chat with'}
                    </Typography>
                  }
                />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
      </Dialog>

      {/* User List Dialog for creating new chat */}
      <Dialog
        open={showUserList}
        onClose={() => {
          setShowUserList(false);
          setUserSearchTerm('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '20px',
              fontFamily: 'Poppins, sans-serif',
              color: '#2C384C',
            }}
          >
            {t('selectUser') || 'Select User'}
          </Typography>
          <IconButton
            onClick={() => {
              setShowUserList(false);
              setUserSearchTerm('');
            }}
            size="small"
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            <Box sx={{ px: 2, pb: 1 }}>
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  height: 40,
                  border: '2px solid #091E4224',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <InputBase
                  placeholder={t('search_placeholder') || 'Search...'}
                  value={userSearchTerm}
                  onChange={(e) => {
                    const newSearch = e.target.value;
                    setUserSearchTerm(newSearch);
                    // Debounce search
                    const timeoutId = setTimeout(() => {
                      loadEligibleUsers(newSearch);
                    }, 300);
                    return () => clearTimeout(timeoutId);
                  }}
                  sx={{
                    ml: 0.5,
                    fontSize: 15,
                    color: '#939393',
                    fontWeight: 500,
                    flex: 1,
                  }}
                />
                <SearchIcon sx={{ fontSize: 22, color: '#888', mr: 0.5 }} />
              </Box>
            </Box>

            {/* Select All option for workers */}
            {selectedChatType === 'worker' && eligibleUsers.length > 0 && (
              <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={
                      eligibleUsers.length > 0 &&
                      eligibleUsers.every((user) =>
                        selectedWorkers.some((w) => w.id === user.id)
                      )
                    }
                    indeterminate={
                      selectedWorkers.length > 0 &&
                      selectedWorkers.length < eligibleUsers.length
                    }
                    onChange={handleSelectAllWorkers}
                    sx={{
                      color: '#006EC2',
                      '&.Mui-checked': {
                        color: '#006EC2',
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#2C384C',
                      fontFamily: 'Poppins, sans-serif',
                      cursor: 'pointer',
                    }}
                    onClick={handleSelectAllWorkers}
                  >
                    {eligibleUsers.length > 0 &&
                    eligibleUsers.every((user) =>
                      selectedWorkers.some((w) => w.id === user.id)
                    )
                      ? t('deselectAll')
  : t('selectAll')
                      }
                  </Typography>
                </Box>
                {selectedWorkers.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: '#666',
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  >
                 {t('selectedCount', {
  selected: selectedWorkers.length,
  total: eligibleUsers.length
})}
                  </Typography>
                )}
              </Box>
            )}

            {/* Show action area when workers are selected */}
            {selectedChatType === 'worker' && selectedWorkers.length > 0 && (
              <Box sx={{ px: 2, pb: 2, borderBottom: '1px solid #e0e0e0' }}>
                {selectedWorkers.length === 1 ? (
                  <>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        mb: 1,
                        color: '#2C384C',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                     {t('selectedEmployee')}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleStartSingleChat}
                      disabled={isLoadingUsers}
                      fullWidth
                      sx={{
                        bgcolor: '#006EC2',
                        '&:hover': { bgcolor: '#0056b3' },
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      {isLoadingUsers ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                       t('startChat')
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        mb: 1,
                        color: '#2C384C',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                     {t('selectedEmployeesSendAll', {
  count: selectedWorkers.length
})}
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      placeholder={t('type Message Placeholder') || 'Type a message...'}
                      value={multiSelectMessage}
                      onChange={(e) => setMultiSelectMessage(e.target.value)}
                      fullWidth
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          fontSize: '14px',
                          fontFamily: 'Poppins, sans-serif',
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectedWorkers([]);
                          setMultiSelectMessage('');
                        }}
                        size="small"
                      >
                       {t("clear")}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={sendMessageToMultipleWorkers}
                        disabled={isSendingMultiMessage || !multiSelectMessage.trim()}
                        sx={{
                          bgcolor: '#006EC2',
                          '&:hover': { bgcolor: '#0056b3' },
                        }}
                        size="small"
                      >
                        {isSendingMultiMessage ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          t('sendToEmployees', { count: selectedWorkers.length })
                        )}
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ maxHeight: selectedChatType === 'worker' && selectedWorkers.length > 1 ? '40vh' : '60vh', overflowY: 'auto', px: 1 }}>
              {isLoadingUsers ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <ListItem key={index} sx={{ px: 1, py: 1 }}>
                    <ListItemAvatar>
                      <Skeleton variant="circular" width={40} height={40} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Skeleton variant="text" width="70%" />}
                      secondary={<Skeleton variant="text" width="50%" />}
                    />
                  </ListItem>
                ))
              ) : eligibleUsers.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          textAlign: 'center',
                          color: '#999',
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      >
                        {t('No users available') || 'No users available'}
                      </Typography>
                    }
                  />
                </ListItem>
              ) : (
                <List>
                  {eligibleUsers.map((user) => {
                    const isSelected = selectedChatType === 'worker' && selectedWorkers.some((w) => w.id === user.id);
                    return (
                    <ListItem
                      key={user.id}
                      button
                        onClick={() => {
                          if (selectedChatType === 'worker') {
                            handleWorkerToggle(user);
                          } else {
                            handleUserSelect(user);
                          }
                        }}
                      sx={{
                        px: 1,
                        borderRadius: 2,
                        mb: 0.5,
                          backgroundColor: isSelected ? '#E3F2FD' : 'transparent',
                          '&:hover': { backgroundColor: isSelected ? '#E3F2FD' : '#f5f5f5' },
                        }}
                      >
                        {selectedChatType === 'worker' && (
                          <Checkbox
                            checked={isSelected}
                            sx={{
                              mr: 1,
                              color: '#006EC2',
                              '&.Mui-checked': {
                                color: '#006EC2',
                              },
                            }}
                          />
                        )}
                      <ListItemAvatar>
                        <Avatar
                          src={resolveProfileImageUrl(user.profile_image) || undefined}
                          alt={user.name || user.email || ''}
                          sx={{ width: 40, height: 40 }}
                          slotProps={avatarSlotPropsForSrc(resolveProfileImageUrl(user.profile_image))}
                        >
                          {user.name?.charAt(0) || user.email?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            sx={{
                              fontWeight: '600',
                              fontSize: '14px',
                              fontFamily: 'Poppins, sans-serif',
                            }}
                          >
                            {user.name}
                          </Typography>
                        }
                        secondary={user.email}
                        secondaryTypographyProps={{
                          fontSize: '13px',
                          color: '#5C5C5C',
                          fontFamily: 'Poppins, sans-serif',
                        }}
                      />
                    </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      {showBroadcastDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowBroadcastDialog(false)}
        >
          <Paper
            sx={{
              width: '90%',
              maxWidth: 600,
              maxHeight: '80vh',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {t('messagesBroadcast.title') || 'Broadcast Message to Workers'}
              </Typography>
              <IconButton onClick={() => setShowBroadcastDialog(false)}>
                <Close />
              </IconButton>
            </Box>

            <TextField
              // label={t('messagesBroadcast.messageLabel') || 'Message'}
              multiline
              rows={3}
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder={
                t('messagesBroadcast.enterMessagePlaceholder') ||
                'Enter your broadcast message...'
              }
              sx={{
                mb: 2,
                '& .MuiInputBase-input': {
                  fontSize: '0.9rem',
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '0.9rem',
                  opacity: 1,
                },
              }}
              fullWidth
            />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {(t('messagesBroadcast.selectWorkersCount', {
                count: selectedWorkersForBroadcast.length,
              }) ||
                `Select Workers (${selectedWorkersForBroadcast.length} selected)`)}
            </Typography>

            <Box sx={{ maxHeight: '40vh', overflowY: 'auto', mb: 2 }}>
              {isLoadingUsers ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {broadcastWorkers.map((worker) => {
                    const isSelected = selectedWorkersForBroadcast.some(
                      (w) => w.id === worker.id
                    );
                    return (
                      <ListItem
                        key={worker.id}
                        button
                        onClick={() => {
                          if (isSelected) {
                            setSelectedWorkersForBroadcast((prev) =>
                              prev.filter((w) => w.id !== worker.id)
                            );
                          } else {
                            setSelectedWorkersForBroadcast((prev) => [
                              ...prev,
                              worker,
                            ]);
                          }
                        }}
                        sx={{
                          bgcolor: isSelected ? '#E3F2FD' : 'transparent',
                          '&:hover': { bgcolor: '#F5F7FA' },
                        }}
                      >
                        <Checkbox checked={isSelected} />
                        <ListItemAvatar>
                          <Avatar
                            src={resolveProfileImageUrl(worker.profile_image) || undefined}
                            alt={worker.name || worker.email || ''}
                            sx={{ width: 40, height: 40 }}
                            slotProps={avatarSlotPropsForSrc(resolveProfileImageUrl(worker.profile_image))}
                          >
                            {worker.name?.charAt(0)?.toUpperCase() || worker.email?.charAt(0)?.toUpperCase() || '?'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={worker.name}
                          secondary={worker.email}
                          primaryTypographyProps={{
                            fontSize: '16px',
                            fontWeight: 500,
                            fontFamily: 'Poppins, sans-serif',
                          }}
                          secondaryTypographyProps={{
                            fontSize: '16px',
                            color: '#5C5C5C',
                            fontFamily: 'Poppins, sans-serif',
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowBroadcastDialog(false);
                  setBroadcastMessage('');
                  setSelectedWorkersForBroadcast([]);
                }}
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                variant="contained"
                onClick={sendBroadcast}
                disabled={isSendingBroadcast || !broadcastMessage.trim() || selectedWorkersForBroadcast.length === 0}
                sx={{ bgcolor: '#006EC2' }}
              >
                {isSendingBroadcast ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  (t('messagesBroadcast.sendToWorkersCount', {
                    count: selectedWorkersForBroadcast.length,
                  }) ||
                    `Send to ${selectedWorkersForBroadcast.length} worker(s)`)
                )}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default UnifiedMessages;

